import { useStore } from '@nanostores/react';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import type { MergedWorkspace } from '@/features/workspaces';
import { useListWorkspaces } from '@/features/workspaces/hooks';
import { authStore } from '@/stores/auth';
import { AppBundle } from '@/utils/app-bundles';
import { useActiveBundle } from './active-bundle';
import { useSampleWorkspace } from '@/utils/sample-workspace';

export const CurrentWorkspaceContext = createContext<MergedWorkspace | null>(null);

export function findWorkspace(workspaces: MergedWorkspace[], id: number) {
  return (
    workspaces.find((workspace) => {
      return workspace.id === id;
    }) ?? null
  );
}

function getDefaultWorkspace(workspaces: MergedWorkspace[]) {
  if (workspaces.length === 0) {
    return null;
  }

  const sortedWorkspaces = workspaces.sort((a, b) => a.id - b.id);

  return sortedWorkspaces[0];
}

const WORKSPACE_ID_URL_REGEX = /\/workspaces\/(\d*)(\/?|\/.*)$/;

export function getWorkspaceIdFromUrl(path: string) {
  const result = path.match(WORKSPACE_ID_URL_REGEX);
  if (result != null && result.length > 2) {
    const match = result[1];

    if (match === '') {
      return null;
    }

    return match;
  }
  return null;
}

export function getWorkspaceIdFromCurrentUrl(): number | null {
  const currentUrl = window.location.href;
  const workspaceIdString = getWorkspaceIdFromUrl(currentUrl);
  const workspaceId = Number(workspaceIdString);
  if (workspaceIdString !== null && !Number.isNaN(workspaceId)) {
    return workspaceId;
  } else {
    // Preserve the null to support possible downstream error checks
    return null;
  }
}

function _useCurrentWorkspace() {
  const workspaces = useListWorkspaces();
  const auth = useStore(authStore);
  const activeApp = useActiveBundle();
  const { sampleAccountId, sampleWorkspaceId } = useSampleWorkspace();
  let { workspaceId: workspaceIdParam } = useParams();

  // Consume activeRouteStore to force updating this hook when the route changes
  useStore(activeRouteStore);

  if (workspaces.isLoading === true && workspaces.data == null) {
    return null;
  }

  if (workspaceIdParam === 'last') {
    if (auth.user?.last_cio_workspace_id != null && auth.user?.last_cio_workspace_id !== '0') {
      workspaceIdParam = auth.user.last_cio_workspace_id;
    } else {
      workspaceIdParam = undefined;
    }
  }

  // If there is a workspaceId parameter, just use that to get the current
  // workspace
  if (workspaceIdParam != null && workspaceIdParam !== '') {
    const workspace = findWorkspace(workspaces.data ?? [], parseInt(workspaceIdParam));

    if (workspace != null) {
      return workspace;
    } else if (String(workspaceIdParam) === String(sampleWorkspaceId) && sampleWorkspaceId != null) {
      // For new accounts, sometimes it appears that it takes a while for the
      // sample workspace data to be loaded on the `useListWorkspaces` hook,
      // likely due to permissions caching.
      // In this case, if the workspace id matches the expected sample
      // workspace id, we return a skeleton workspace object with the sample
      // workspace and account ids to prevent issues.
      return {
        id: sampleWorkspaceId,
        account_id: sampleAccountId,
        name: 'Sample Workspace',
        color: '',
        cio_initialized: true,
        cio_workspace_id: sampleWorkspaceId,
        cdp_workspace_id: sampleWorkspaceId,
        cdp_initialized: true,
        user_ids: [],
        user_roles: null,
      };
    } else {
      // If the workspace doesn't exist, return the default (first) workspace
      return getDefaultWorkspace(workspaces.data ?? []);
    }
  }

  let activeWorkspaceId: string | null = null;

  let workspace: MergedWorkspace | null = null;

  // If we're in the Pipelines app, see if there is a "last used workspace"
  // local storage item
  if (activeApp === AppBundle.Pipelines) {
    const lastLocalStorageWorkspaceValue = localStorage.getItem('cio:cdp:workspaceId');
    if (lastLocalStorageWorkspaceValue != null) {
      const stringValue = lastLocalStorageWorkspaceValue.toString();
      if (stringValue !== '') {
        activeWorkspaceId = stringValue;
      }
    }
  }

  // If we still don't have an active workspace id, fall back to last Journeys
  // workspace, if there is one (regardless of which app is "active")
  if (activeWorkspaceId == null) {
    if (auth.user?.last_cio_workspace_id != null && auth.user?.last_cio_workspace_id !== '0') {
      activeWorkspaceId = auth.user.last_cio_workspace_id;
    }
  }

  if (activeWorkspaceId != null && workspace == null) {
    workspace = findWorkspace(workspaces.data ?? [], parseInt(activeWorkspaceId));
  }

  // If we still don't have an active workspace, fall back to the default
  if (workspace == null) {
    workspace = getDefaultWorkspace(workspaces.data ?? []);
  }

  return workspace;
}

export function useCurrentWorkspace() {
  return useContext(CurrentWorkspaceContext);
}

export function CurrentWorkspaceProvider({ children }: { children: React.ReactNode }) {
  const currentWorkspace = _useCurrentWorkspace();

  return <CurrentWorkspaceContext.Provider value={currentWorkspace}>{children}</CurrentWorkspaceContext.Provider>;
}
